/**
 * bakeReload — a per-environment "baked artifacts changed" signal (ENG-592 / the
 * navmesh "build did nothing" bug).
 *
 * The problem it fixes: every baked-artifact volume (NavMeshVolume,
 * LightProbeVolume, ReflectionCaptureVolume, VoxelVolume, the collision mesh)
 * fetches its file ONCE, in an effect keyed on [splatUrl, splatVersion]. A bake
 * that completes AFTER the environment loaded writes a new navmesh.json /
 * lightprobes.json / … beside the .ply, but nothing re-fetches it — so the row
 * keeps reporting NOT BUILT until a full page reload, which reads to the user as
 * "Build Navmesh did nothing". splatVersion can't carry this signal: it also
 * cache-busts the ~130 MB splat download (Scene passes it to SplatEnvironment),
 * so bumping it on a bake would re-download the whole environment.
 *
 * So this is a SEPARATE signal, consumed only by the small artifact volumes.
 * bakeStatusStore (which observes the baking→ready transition) is the producer;
 * Scene reads the ACTIVE environment's generation and passes it as a reloadKey
 * the volumes fold into their fetch-effect deps. Keying by assetId keeps it
 * precise: a bake finishing on some OTHER environment leaves the active one's
 * generation untouched, so the visible scene doesn't re-fetch for nothing.
 *
 * Pure: no network, no DOM, no THREE — so it imports cleanly under node --test
 * (bakeStatusStore itself cannot; that is why the counter lives here, not there).
 */

const generation = new Map() // assetId -> monotonically increasing count
const listeners = new Set()

/**
 * Record that `assetId`'s baked artifacts may have changed (a bake completed).
 * Increments that asset's generation and notifies subscribers. No-op without an
 * id, so callers don't have to guard.
 */
export function bumpBakeGeneration(assetId) {
  if (!assetId) return
  generation.set(assetId, (generation.get(assetId) || 0) + 1)
  for (const fn of listeners) fn(assetId)
}

// Console / manual-test seam: read the live generations, and force a reload of a
// given env's baked artifacts without a real bake (e.g. after re-uploading a
// navmesh.json by hand). Mirrors the repo's window.__*__ debug-global pattern.
if (typeof window !== 'undefined') {
  window.__BAKE_RELOAD__ = { get: getBakeGeneration, bump: bumpBakeGeneration }
}

/**
 * The current generation for an environment asset (0 if no bake has completed
 * for it this session). Consumers put this in an effect's deps so a change
 * re-runs the fetch.
 */
export function getBakeGeneration(assetId) {
  return (assetId && generation.get(assetId)) || 0
}

/** Subscribe to generation bumps. Callback receives the assetId. */
export function subscribeBakeReload(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/** Test-only: clear all state so specs don't leak generations into each other. */
export function __resetBakeReload() {
  generation.clear()
  listeners.clear()
}
